/**
 * Knowledge — Beatly's strategy pool (cowork /v1/knowledge). Staff add platform updates, research and trends
 * (files, links, pasted notes); the scout adds more on its own. Each source is distilled into snippets every agent
 * can draw on. Staff only: reached from the BO sidebar, never in Omni's own nav.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, ExternalLink, FileText, Link2, Loader2, Pencil, Play, RotateCw, Search, Trash2, Type, Upload, X } from 'lucide-react';
import { coworkApi, type KnowledgeKind, type KnowledgeScoutRun, type KnowledgeSnippet, type KnowledgeStatus } from '@/cowork/omniad/api/coworkApi';
import { useStaffState } from '@/cowork/useIsStaff';

const card = 'rounded-2xl border border-[#eaecf0] bg-white';
const input = 'w-full rounded-lg border border-[#d0d5dd] bg-white px-3 py-2 text-[13px] text-[#101828] placeholder:text-[#98a2b3] focus:border-[#7f56d9] focus:outline-none';
const primary = 'inline-flex items-center gap-1.5 rounded-lg bg-[#7f56d9] px-3.5 py-2 text-[13px] font-medium text-white hover:bg-[#6941c6] disabled:cursor-not-allowed disabled:opacity-50';
const secondary = 'inline-flex items-center gap-1.5 rounded-lg border border-[#d0d5dd] bg-white px-3 py-1.5 text-[12.5px] font-medium text-[#344054] hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-50';
const tag = 'rounded-full bg-[#f2f4f7] px-2 py-0.5 text-[11px] text-[#475467]';
const KEY = ['cowork', 'knowledge'] as const;
const ACCEPT = '.pdf,.pptx,.docx,.txt,.md,.mp4,.mov,.mp3,.m4a,.wav,.webm';
const KINDS: Array<{ id: KnowledgeKind; label: string }> = [{ id: 'upload', label: 'File' }, { id: 'link', label: 'Link' }, { id: 'text', label: 'Notes' }, { id: 'scout', label: 'Scout' }, { id: 'trends', label: 'Trends' }];
const PLATFORMS = ['instagram', 'tiktok', 'youtube', 'facebook', 'meta', 'snapchat', 'pinterest', 'linkedin', 'x', 'google', 'general'];
const kindLabel = (k: KnowledgeKind) => KINDS.find((x) => x.id === k)?.label || k;
// A publish date may be just a year ("2026") or a month ("2026-03"): show only what is known.
const day = (iso?: string | null) => {
  if (!iso) return '—';
  if (/^\d{4}$/.test(iso)) return iso;
  if (/^\d{4}-\d{2}$/.test(iso)) return new Date(`${iso}-01T12:00:00`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const MAX_DOC_BYTES = 60 * 1024 * 1024;
const isMedia = (f: File) => /^(video|audio)\//.test(f.type) || /\.(mp4|mov|m4a|mp3|wav|webm|mkv|m4v)$/i.test(f.name);
const ago = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const errMsg = (e: unknown) => (e as Error)?.message || 'Something went wrong';

export function KnowledgePage() {
  const { staff, failed } = useStaffState();
  if (staff === null && !failed) return <div className="flex items-center gap-2 px-6 py-10 text-[13px] text-[#667085]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (!staff) {
    return (
      <div className="mx-auto max-w-[720px] px-6 py-10">
        <div className={`${card} p-6`}><h1 className="text-[18px] text-[#101828]" style={{ fontWeight: 700 }}>Staff only</h1><p className="mt-1 text-[13px] text-[#667085]">The knowledge pool is managed by Beatly staff. Agents already use it in your work.</p></div>
      </div>
    );
  }
  return <KnowledgeWorkspace />;
}

function KnowledgeWorkspace() {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <div className="mx-auto max-w-[1200px] px-6 py-6">
      <div className="mb-5">
        <h1 className="text-[24px] text-[#101828]" style={{ fontWeight: 700 }}>Knowledge</h1>
        <p className="mt-1 max-w-2xl text-[13px] text-[#667085]">Beatly's strategy pool — platform updates, research and trends every agent can use. Staff only.</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2"><AddPanel /></div>
        <ScoutCard />
      </div>
      <TryQuestion />
      <ItemsList onOpen={setOpenId} />
      {openId && <ItemDrawer id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

// ── Add: files / link / pasted text ─────────────────────────────────────────────────────────────────────────

type AddTab = 'upload' | 'link' | 'text';

function AddPanel() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<AddTab>('upload');
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [publishedAt, setPublishedAt] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => { setFiles([]); setUrl(''); setTitle(''); setText(''); setSourceName(''); setPublishedAt(''); };
  const add = useMutation({
    mutationFn: async (): Promise<number> => {
      const meta = { sourceName: sourceName.trim() || undefined, publishedAt: publishedAt || undefined };
      if (tab === 'upload') return (await coworkApi.knowledge.upload(files, { ...meta, title: files.length === 1 && title.trim() ? title.trim() : undefined })).items.length;
      if (tab === 'link') { await coworkApi.knowledge.addLink({ url: url.trim(), title: title.trim() || undefined, sourceName: meta.sourceName }); return 1; }
      await coworkApi.knowledge.addText({ title: title.trim() || 'Notes', text, ...meta }); return 1;
    },
    onSuccess: (n) => { toast.success(n === 1 ? 'Added — reading it now' : `Added ${n} sources — reading them now`); reset(); void qc.invalidateQueries({ queryKey: [...KEY, 'items'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  // The server takes documents up to 60 MB (videos and audio up to 800 MB): say so before a long upload, not after.
  const addFiles = (list: FileList | File[] | null) => {
    if (!list) return;
    const all = Array.from(list);
    const tooBig = all.filter((f) => (isMedia(f) ? f.size > 800 * 1024 * 1024 : f.size > MAX_DOC_BYTES));
    if (tooBig.length) toast.error(`${tooBig.map((f) => f.name).join(', ')}: documents up to 60 MB, videos and audio up to 800 MB.`);
    setFiles((prev) => [...prev, ...all.filter((f) => !tooBig.includes(f))].slice(0, 10));
  };
  const ready = tab === 'upload' ? files.length > 0 : tab === 'link' ? /^https?:\/\/\S+/.test(url.trim()) : text.trim().length >= 150;
  const tabs: Array<{ id: AddTab; label: string; icon: typeof Upload }> = [{ id: 'upload', label: 'Upload files', icon: Upload }, { id: 'link', label: 'Link', icon: Link2 }, { id: 'text', label: 'Paste text', icon: Type }];

  return (
    <section className={`${card} p-5`}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>Add to the pool</h2>
        <div role="tablist" className="flex rounded-lg border border-[#eaecf0] bg-[#f9fafb] p-0.5">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12.5px] ${tab === t.id ? 'bg-white font-medium text-[#101828] shadow-sm' : 'text-[#667085] hover:text-[#344054]'}`}><t.icon className="h-3.5 w-3.5" />{t.label}</button>
          ))}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {tab === 'upload' && (
          <>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
              data-testid="knowledge-dropzone"
              className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-6 text-center ${dragging ? 'border-[#7f56d9] bg-[#f9f5ff]' : 'border-[#d0d5dd] bg-[#fcfcfd] hover:bg-[#f9fafb]'}`}
            >
              <Upload className="h-5 w-5 text-[#7f56d9]" />
              <div className="mt-2 text-[13px] text-[#344054]"><b>Drop files here</b> or click to choose</div>
              <div className="mt-0.5 text-[11.5px] text-[#98a2b3]">PDF, PowerPoint, Word, text, video or audio · up to 10 at once</div>
              <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" aria-label="Choose files" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            </div>
            {files.length > 0 && (
              <ul className="space-y-1">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-[#f9fafb] px-3 py-1.5 text-[12.5px] text-[#344054]">
                    <span className="flex min-w-0 items-center gap-2"><FileText className="h-3.5 w-3.5 shrink-0 text-[#667085]" /><span className="truncate">{f.name}</span></span>
                    <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} className="text-[#98a2b3] hover:text-[#344054]"><X className="h-3.5 w-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
            {files.length === 1 && <input className={input} placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />}
          </>
        )}
        {tab === 'link' && (
          <>
            <input className={input} placeholder="https://… an article, report or platform update" aria-label="Link" value={url} onChange={(e) => setUrl(e.target.value)} />
            <input className={input} placeholder="Title (optional — we read it from the page)" value={title} onChange={(e) => setTitle(e.target.value)} />
          </>
        )}
        {tab === 'text' && (
          <>
            <input className={input} placeholder="Title" aria-label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <textarea className={`${input} min-h-[140px]`} placeholder="Paste notes, a transcript or an excerpt (at least a paragraph)" aria-label="Text" value={text} onChange={(e) => setText(e.target.value)} />
            {text.length > 0 && text.trim().length < 150 && <div className="text-[11.5px] text-[#98a2b3]">{150 - text.trim().length} more characters needed</div>}
          </>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <input className={input} placeholder="Source name (optional, e.g. TikTok Newsroom)" aria-label="Source name" value={sourceName} onChange={(e) => setSourceName(e.target.value)} />
          {tab !== 'link' && <input className={input} type="date" aria-label="Published" title="Published (optional)" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} />}
        </div>
        <div className="flex justify-end">
          <button type="button" className={primary} disabled={!ready || add.isPending} onClick={() => add.mutate()}>{add.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}Add to the pool</button>
        </div>
      </div>
    </section>
  );
}

// ── Scout ───────────────────────────────────────────────────────────────────────────────────────────────────

function ScoutCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [...KEY, 'scout'], queryFn: () => coworkApi.knowledge.scout(), refetchInterval: (query) => (query.state.data?.running ? 5000 : false) });
  const run = useMutation({
    mutationFn: () => coworkApi.knowledge.runScout({}),
    onSuccess: () => { toast.success('The scout is on its way'); void qc.invalidateQueries({ queryKey: [...KEY, 'scout'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  // When a run finishes, the items it added show up in the list.
  const wasRunning = useRef(false);
  useEffect(() => { const r = !!q.data?.running; if (wasRunning.current && !r) void qc.invalidateQueries({ queryKey: [...KEY, 'items'] }); wasRunning.current = r; }, [q.data?.running, qc]);
  const s = q.data;
  const running = !!s?.running;

  return (
    <section className={`${card} flex flex-col p-5`}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>Scout</h2>
        {s && <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.enabled ? 'bg-[#ecfdf3] text-[#027a48]' : 'bg-[#f2f4f7] text-[#475467]'}`}>{s.enabled ? 'Runs on schedule' : 'Schedule off'}</span>}
      </div>
      <p className="mt-1 text-[12.5px] text-[#667085]">Reads platform newsrooms and research sources, adds what is new and relevant.</p>
      {q.isLoading ? <div className="mt-4 flex items-center gap-2 text-[12.5px] text-[#667085]"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div> : q.isError ? <div className="mt-4 text-[12.5px] text-[#b42318]">{errMsg(q.error)}</div> : s && (
        <>
          {running && s.current && <div className="mt-3 flex items-center gap-2 rounded-lg bg-[#f9f5ff] px-3 py-2 text-[12.5px] text-[#6941c6]"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Running since {ago(s.current.startedAt)} · {s.current.added.length} added so far</div>}
          {s.last ? <ScoutRunSummary run={s.last} /> : !running && <div className="mt-3 text-[12.5px] text-[#667085]">No run yet.</div>}
          {s.sources.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">{s.sources.map((src) => <span key={src.id} className={tag} title={src.kind}>{src.name}</span>)}</div>
          )}
        </>
      )}
      <div className="mt-auto pt-4">
        <button type="button" className={`${primary} w-full justify-center`} disabled={running || run.isPending || !s} onClick={() => run.mutate()}>{running || run.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}{running ? 'Scout is running…' : 'Run the scout now'}</button>
      </div>
    </section>
  );
}

function ScoutRunSummary({ run }: { run: KnowledgeScoutRun }) {
  return (
    <div className="mt-3 space-y-1 text-[12.5px] text-[#344054]" data-testid="scout-last">
      <div>Last run <b>{ago(run.finishedAt || run.startedAt)}</b>{run.by ? <span className="text-[#98a2b3]"> · by {run.by}</span> : null}</div>
      <div><b>{run.added.length}</b> added · {run.considered} considered{run.trends ? ` · trends for ${run.trends.markets} markets` : ''}</div>
      {run.errors.length > 0 && (
        <details className="text-[12px]">
          <summary className="cursor-pointer text-[#b54708]">{run.errors.length} error{run.errors.length === 1 ? '' : 's'}</summary>
          <ul className="mt-1 max-h-32 space-y-0.5 overflow-auto text-[#667085]">{run.errors.map((e, i) => <li key={i} className="break-words">{e}</li>)}</ul>
        </details>
      )}
    </div>
  );
}

// ── Try a question ─────────────────────────────────────────────────────────────────────────────────────────

function TryQuestion() {
  const [q, setQ] = useState('');
  const search = useMutation({ mutationFn: (text: string) => coworkApi.knowledge.search(text), onError: (e) => toast.error(errMsg(e)) });
  const results = search.data?.results;
  return (
    <section className={`${card} mt-4 p-5`}>
      <h2 className="text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>Try a question</h2>
      <p className="mt-0.5 text-[12.5px] text-[#667085]">See which snippets an agent would get for it.</p>
      <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (q.trim()) search.mutate(q.trim()); }}>
        <input className={input} placeholder="e.g. What changed in TikTok Shop this quarter?" aria-label="Question" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" className={primary} disabled={!q.trim() || search.isPending}>{search.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}Ask</button>
      </form>
      {results && (results.length === 0 ? <div className="mt-3 text-[12.5px] text-[#667085]">Nothing in the pool matches that yet.</div> : (
        <ol className="mt-3 space-y-2" aria-label="Search results">
          {results.map((r, i) => (
            <li key={i} className="rounded-lg bg-[#f9fafb] px-3 py-2">
              <div className="flex items-baseline justify-between gap-3"><span className="text-[12.5px] font-medium text-[#101828]">{r.label}</span><span className="shrink-0 text-[11px] tabular-nums text-[#98a2b3]">{r.score.toFixed(2)}</span></div>
              <p className="mt-0.5 text-[12.5px] leading-snug text-[#344054]">{r.text}</p>
            </li>
          ))}
        </ol>
      ))}
    </section>
  );
}

// ── Items ───────────────────────────────────────────────────────────────────────────────────────────────────

function StatusChip({ status, error }: { status: KnowledgeStatus; error?: string | null }) {
  if (status === 'processing') return <span className="inline-flex items-center gap-1 rounded-full bg-[#f9f5ff] px-2 py-0.5 text-[11px] font-medium text-[#6941c6]"><Loader2 className="h-3 w-3 animate-spin" />Reading</span>;
  if (status === 'ready') return <span className="rounded-full bg-[#ecfdf3] px-2 py-0.5 text-[11px] font-medium text-[#027a48]">Ready</span>;
  if (status === 'failed') return <span title={error || 'Failed'} className="inline-flex cursor-help items-center gap-1 rounded-full bg-[#fef3f2] px-2 py-0.5 text-[11px] font-medium text-[#b42318]"><AlertTriangle className="h-3 w-3" />Failed</span>;
  return <span title="Read, but nothing a strategist would use" className="rounded-full bg-[#f2f4f7] px-2 py-0.5 text-[11px] font-medium text-[#475467]">Not relevant</span>;
}

function StaleToggle({ stale, onChange, disabled, label }: { stale: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={stale} aria-label={label} disabled={disabled} onClick={(e) => { e.stopPropagation(); onChange(!stale); }} title={stale ? 'Stale — agents skip it. Click to use again.' : 'Current — click to mark stale'} className={`inline-flex items-center gap-1.5 text-[11.5px] ${stale ? 'text-[#b54708]' : 'text-[#667085]'} disabled:opacity-50`}>
      <span className={`relative h-4 w-7 rounded-full transition-colors ${stale ? 'bg-[#f79009]' : 'bg-[#d0d5dd]'}`}><span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${stale ? 'left-3.5' : 'left-0.5'}`} /></span>
      {stale ? 'Stale' : 'Current'}
    </button>
  );
}

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function ItemsList({ onOpen }: { onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const [platform, setPlatform] = useState('');
  const q = useDebounced(search.trim());
  const list = useQuery({
    queryKey: [...KEY, 'items', q, kind, platform],
    queryFn: () => coworkApi.knowledge.list({ q, kind, platform }),
    refetchInterval: (query) => (query.state.data?.items.some((i) => i.status === 'processing') ? 4000 : false),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });
  const stale = useMutation({ mutationFn: ({ id, v }: { id: string; v: boolean }) => coworkApi.knowledge.setStale(id, v), onSuccess: invalidate, onError: (e) => toast.error(errMsg(e)) });
  const remove = useMutation({ mutationFn: (id: string) => coworkApi.knowledge.remove(id), onSuccess: () => { toast.success('Removed from the pool'); void invalidate(); }, onError: (e) => toast.error(errMsg(e)) });
  const items = list.data?.items || [];

  return (
    <section className={`${card} mt-4 overflow-hidden`}>
      <div className="flex flex-wrap items-center gap-2 border-b border-[#eaecf0] px-5 py-3">
        <h2 className="mr-auto text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>Sources{list.data ? <span className="ml-1.5 text-[12.5px] font-normal text-[#98a2b3]">{items.length}</span> : null}</h2>
        <div className="relative"><Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#98a2b3]" /><input className={`${input} w-56 py-1.5 pl-8`} placeholder="Search sources" aria-label="Search sources" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
        <select aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)} className="rounded-lg border border-[#d0d5dd] bg-white px-2.5 py-1.5 text-[13px] text-[#344054]"><option value="">All kinds</option>{KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}</select>
        <select aria-label="Platform" value={platform} onChange={(e) => setPlatform(e.target.value)} className="rounded-lg border border-[#d0d5dd] bg-white px-2.5 py-1.5 text-[13px] text-[#344054]"><option value="">All platforms</option>{PLATFORMS.map((p) => <option key={p} value={p}>{p === 'x' ? 'X' : p[0].toUpperCase() + p.slice(1)}</option>)}</select>
      </div>
      {list.isLoading ? <div className="flex items-center gap-2 px-5 py-8 text-[13px] text-[#667085]"><Loader2 className="h-4 w-4 animate-spin" /> Loading the pool…</div>
        : list.isError ? <div className="px-5 py-8 text-[13px] text-[#b42318]">{errMsg(list.error)}</div>
        : !items.length ? <div className="px-5 py-10 text-center text-[13px] text-[#667085]">{q || kind || platform ? 'Nothing matches these filters.' : 'The pool is empty. Add a report, a link or some notes above, or run the scout.'}</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead className="bg-[#f9fafb] text-[11.5px] text-[#667085]"><tr><th className="px-5 py-2 font-medium">Title</th><th className="px-3 py-2 font-medium">Kind</th><th className="px-3 py-2 font-medium">Status</th><th className="px-3 py-2 font-medium">Date</th><th className="px-3 py-2 text-right font-medium">Snippets</th><th className="px-3 py-2 font-medium">Stale</th><th className="w-10 px-3 py-2" /></tr></thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} onClick={() => onOpen(it.id)} className={`cursor-pointer border-t border-[#f2f4f7] hover:bg-[#fcfcfd] ${it.stale ? 'opacity-60' : ''}`}>
                    <td className="max-w-[420px] px-5 py-2.5"><div className="truncate font-medium text-[#101828]">{it.title}</div><div className="truncate text-[11.5px] text-[#98a2b3]">{it.sourceName || it.fileName || (it.sourceUrl ? safeHost(it.sourceUrl) : '') || (it.addedBy ? `added by ${it.addedBy}` : '')}</div></td>
                    <td className="px-3 py-2.5"><span className={tag}>{kindLabel(it.kind)}</span></td>
                    <td className="px-3 py-2.5"><StatusChip status={it.status} error={it.error} /></td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-[12.5px] text-[#475467]">{day(it.publishedAt || it.addedAt)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-[#344054]">{it.snippets}</td>
                    <td className="px-3 py-2.5"><StaleToggle stale={it.stale} label={`Stale: ${it.title}`} disabled={stale.isPending} onChange={(v) => stale.mutate({ id: it.id, v })} /></td>
                    <td className="px-3 py-2.5"><button type="button" aria-label={`Delete ${it.title}`} onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${it.title}" and its ${it.snippets} snippet${it.snippets === 1 ? '' : 's'}?`)) remove.mutate(it.id); }} className="rounded p-1 text-[#98a2b3] hover:bg-[#fef3f2] hover:text-[#b42318]"><Trash2 className="h-3.5 w-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </section>
  );
}

function safeHost(u: string): string { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return u; } }

// ── Drawer: one source and its snippets ────────────────────────────────────────────────────────────────────

function ItemDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [...KEY, 'item', id], queryFn: () => coworkApi.knowledge.get(id), refetchInterval: (query) => (query.state.data?.item.status === 'processing' ? 4000 : false) });
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });
  const stale = useMutation({ mutationFn: (v: boolean) => coworkApi.knowledge.setStale(id, v), onSuccess: invalidate, onError: (e) => toast.error(errMsg(e)) });
  const reprocess = useMutation({ mutationFn: () => coworkApi.knowledge.reprocess(id), onSuccess: () => { toast.success('Reading it again'); void invalidate(); onClose(); }, onError: (e) => toast.error(errMsg(e)) });
  const remove = useMutation({ mutationFn: () => coworkApi.knowledge.remove(id), onSuccess: () => { toast.success('Removed from the pool'); void invalidate(); onClose(); }, onError: (e) => toast.error(errMsg(e)) });
  useEffect(() => { const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey); }, [onClose]);
  const item = q.data?.item;
  const snippets = q.data?.snippets || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-[#101828]/30" onClick={onClose} aria-hidden="true" />
      <aside role="dialog" aria-label={item?.title || 'Source'} className="relative flex h-full w-full max-w-[560px] flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-[#eaecf0] px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-[16px] leading-snug text-[#101828]" style={{ fontWeight: 700 }}>{item?.title || 'Loading…'}</h2>
            {item && <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11.5px] text-[#667085]"><span className={tag}>{kindLabel(item.kind)}</span><StatusChip status={item.status} error={item.error} /><span>{day(item.publishedAt || item.addedAt)}</span>{item.sourceName && <span>· {item.sourceName}</span>}</div>}
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="rounded p-1 text-[#667085] hover:bg-[#f2f4f7]"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {q.isLoading ? <div className="flex items-center gap-2 text-[13px] text-[#667085]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> : q.isError ? <div className="text-[13px] text-[#b42318]">{errMsg(q.error)}</div> : item && (
            <>
              {item.status === 'failed' && item.error && <div className="mb-3 rounded-lg bg-[#fef3f2] px-3 py-2 text-[12.5px] text-[#b42318]">{item.error}</div>}
              {item.summary && <p className="text-[13px] leading-relaxed text-[#344054]">{item.summary}</p>}
              <div className="mt-3 space-y-1 text-[12px] text-[#667085]">
                {item.sourceUrl && <a href={item.sourceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#6941c6] hover:underline">{safeHost(item.sourceUrl)} <ExternalLink className="h-3 w-3" /></a>}
                {item.fileName && <div>File: {item.fileName}</div>}
                <div>Added {ago(item.addedAt)}{item.addedBy ? ` by ${item.addedBy}` : ''}</div>
              </div>
              {(item.platforms.length > 0 || item.topics.length > 0) && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {item.platforms.map((p) => <span key={`p-${p}`} className="rounded-full bg-[#f4ebff] px-2 py-0.5 text-[11px] text-[#6941c6]">{p}</span>)}
                  {item.topics.map((t) => <span key={`t-${t}`} className={tag}>{t}</span>)}
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <StaleToggle stale={item.stale} label="Mark the whole source stale" disabled={stale.isPending} onChange={(v) => stale.mutate(v)} />
                <span className="flex-1" />
                {item.sourceUrl && <button type="button" className={secondary} disabled={reprocess.isPending || item.status === 'processing'} onClick={() => reprocess.mutate()}><RotateCw className="h-3.5 w-3.5" />Read again</button>}
                <button type="button" className={`${secondary} text-[#b42318]`} disabled={remove.isPending} onClick={() => { if (window.confirm(`Delete "${item.title}" and its snippets?`)) remove.mutate(); }}><Trash2 className="h-3.5 w-3.5" />Delete</button>
              </div>
              <h3 className="mt-6 text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>Snippets <span className="font-normal text-[#98a2b3]">{snippets.length}</span></h3>
              {item.status === 'processing' ? <div className="mt-2 flex items-center gap-2 text-[12.5px] text-[#667085]"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading — snippets appear here in a moment.</div>
                : !snippets.length ? <div className="mt-2 text-[12.5px] text-[#667085]">No snippets{item.status === 'skipped' ? ' — nothing here a strategist would use.' : '.'}</div>
                : <ul className="mt-2 space-y-2">{snippets.map((s) => <SnippetRow key={s.id} snippet={s} itemId={id} />)}</ul>}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function SnippetRow({ snippet, itemId }: { snippet: KnowledgeSnippet; itemId: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(snippet.label);
  const [text, setText] = useState(snippet.text);
  const refresh = () => { void qc.invalidateQueries({ queryKey: [...KEY, 'item', itemId] }); void qc.invalidateQueries({ queryKey: [...KEY, 'items'] }); };
  const save = useMutation({ mutationFn: (body: { label?: string; text?: string; stale?: boolean }) => coworkApi.knowledge.updateSnippet(snippet.id, body), onSuccess: () => { setEditing(false); refresh(); }, onError: (e) => toast.error(errMsg(e)) });
  const remove = useMutation({ mutationFn: () => coworkApi.knowledge.removeSnippet(snippet.id), onSuccess: refresh, onError: (e) => toast.error(errMsg(e)) });

  if (editing) {
    return (
      <li className="rounded-lg border border-[#d6bbfb] bg-[#fcfaff] p-3">
        <input className={input} aria-label="Snippet label" value={label} onChange={(e) => setLabel(e.target.value)} />
        <textarea className={`${input} mt-2 min-h-[90px]`} aria-label="Snippet text" value={text} onChange={(e) => setText(e.target.value)} />
        <div className="mt-2 flex justify-end gap-2">
          <button type="button" className={secondary} onClick={() => { setEditing(false); setLabel(snippet.label); setText(snippet.text); }}>Cancel</button>
          <button type="button" className={primary} disabled={!text.trim() || save.isPending} onClick={() => save.mutate({ label: label.trim(), text: text.trim() })}>{save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Save</button>
        </div>
      </li>
    );
  }
  return (
    <li className={`group rounded-lg bg-[#f9fafb] px-3 py-2 ${snippet.stale ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 text-[12.5px] font-medium text-[#101828]">{snippet.label}</div>
        <div className="flex shrink-0 items-center gap-1">
          <StaleToggle stale={snippet.stale} label={`Stale: ${snippet.label}`} disabled={save.isPending} onChange={(v) => save.mutate({ stale: v })} />
          <button type="button" aria-label={`Edit ${snippet.label}`} onClick={() => setEditing(true)} className="rounded p-1 text-[#98a2b3] hover:bg-white hover:text-[#344054]"><Pencil className="h-3.5 w-3.5" /></button>
          <button type="button" aria-label={`Delete ${snippet.label}`} disabled={remove.isPending} onClick={() => { if (window.confirm('Delete this snippet?')) remove.mutate(); }} className="rounded p-1 text-[#98a2b3] hover:bg-white hover:text-[#b42318]"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <p className="mt-0.5 text-[12.5px] leading-snug text-[#344054]">{snippet.text}</p>
      {(snippet.platforms.length > 0 || snippet.topics.length > 0) && <div className="mt-1 flex flex-wrap gap-1">{snippet.platforms.map((p) => <span key={`p-${p}`} className="rounded-full bg-[#f4ebff] px-1.5 py-px text-[10.5px] text-[#6941c6]">{p}</span>)}{snippet.topics.map((t) => <span key={`t-${t}`} className="rounded-full bg-white px-1.5 py-px text-[10.5px] text-[#667085]">{t}</span>)}</div>}
    </li>
  );
}

